/**
 * useTags — team-defined tag chips with single/double click semantics.
 *
 * Extracted from EstadisticasNew.js:
 *  - Tag loading (/api/stats/tags/:groupeId, gated on statsMode in {LIVE, VIDEO})
 *  - openTagsDialog / selectedTag / selectedPlayersForTag state
 *  - handleTagClick (debounced single vs double click via tagClickTimerRef)
 *  - togglePlayerForTag / selectAllPlayersForTag
 *  - buildTagAction — pure builder used by handleQuickTagApply and
 *    handleApplyTag to produce a tag action object with the correct
 *    timestamp logic for both modes (note the extra -5s offset on tags).
 *
 * The actual append-to-actionsLog is left to the caller so we don't have to
 * couple this hook to useStatsSession.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import api from '../../../api/axiosConfig';
import { STATS_MODE, TAG_CLICK_DELAY_MS, TAG_EXTRA_OFFSET } from '../constants';

export function useTags({ groupeId, statsMode }) {
  const [availableTags, setAvailableTags] = useState([]);
  const [loadingTags, setLoadingTags] = useState(false);

  const [openTagsDialog, setOpenTagsDialog] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [selectedPlayersForTag, setSelectedPlayersForTag] = useState([]);

  const tagClickTimerRef = useRef(null);

  // Load tags whenever the user enters a stats mode (LIVE or VIDEO)
  useEffect(() => {
    if (statsMode !== STATS_MODE.VIDEO && statsMode !== STATS_MODE.LIVE) return;
    if (!groupeId) return;

    setLoadingTags(true);
    api.get(`/api/stats/tags/${groupeId}`)
      .then((response) => {
        if (response.data) setAvailableTags(response.data);
      })
      .catch((error) => {
        console.error('Error al obtener los tags:', error);
      })
      .finally(() => setLoadingTags(false));
  }, [groupeId, statsMode]);

  // Cleanup the click debounce timer on unmount
  useEffect(() => {
    return () => {
      if (tagClickTimerRef.current) {
        clearTimeout(tagClickTimerRef.current);
        tagClickTimerRef.current = null;
      }
    };
  }, []);

  /**
   * Pure builder mirroring the inline tag-action object construction in
   * handleQuickTagApply / handleApplyTag.
   *
   * Returns a `null`-safe action object ready to be pushed onto actionsLog.
   *
   * @param {object} tag                  the tag (must have .libelle)
   * @param {object} player               the player (must have .id, .nom)
   * @param {object} ctx                  context snapshot
   * @param {number} ctx.currentPeriod
   * @param {string} ctx.mode             STATS_MODE.LIVE or STATS_MODE.VIDEO
   * @param {number} ctx.videoCurrentTime (video mode)
   * @param {number} ctx.videoOffset      (video mode)
   * @param {number} ctx.realTime         (live mode — realTimeRef.current)
   */
  const buildTagAction = useCallback((tag, player, ctx) => {
    if (!tag || !player) return null;
    const tagAction = `Tag: ${tag.libelle}`;
    const { currentPeriod, mode, videoCurrentTime, videoOffset, realTime } = ctx;

    if (mode === STATS_MODE.VIDEO) {
      // Tags are longer actions, so apply extra offset on top of videoOffset
      const tagTimestamp = Math.max(0, videoCurrentTime + videoOffset + TAG_EXTRA_OFFSET);
      return {
        playerId: player.id,
        playerName: player.nom,
        action: tagAction,
        period: currentPeriod,
        timeSec: Math.floor(tagTimestamp),
        videoTimestamp: tagTimestamp,
        realTimeTimestamp: null,
        isVideoMode: true,
        isLiveMode: false,
      };
    }

    if (mode === STATS_MODE.LIVE) {
      return {
        playerId: player.id,
        playerName: player.nom,
        action: tagAction,
        period: currentPeriod,
        timeSec: realTime,
        videoTimestamp: null,
        realTimeTimestamp: realTime,
        isVideoMode: false,
        isLiveMode: true,
      };
    }

    return null;
  }, []);

  /**
   * Single/double click discriminator. Mirrors handleTagClick().
   *
   * @param {object} tag
   * @param {object} callbacks
   * @param {(tag) => void} callbacks.onSingleClick — apply tag to all players
   * @param {(tag) => void} callbacks.onDoubleClick — open selective dialog
   */
  const handleTagClick = useCallback((tag, callbacks) => {
    if (tagClickTimerRef.current) {
      clearTimeout(tagClickTimerRef.current);
      tagClickTimerRef.current = null;
      if (callbacks && typeof callbacks.onDoubleClick === 'function') {
        callbacks.onDoubleClick(tag);
      }
    } else {
      tagClickTimerRef.current = setTimeout(() => {
        if (callbacks && typeof callbacks.onSingleClick === 'function') {
          callbacks.onSingleClick(tag);
        }
        tagClickTimerRef.current = null;
      }, TAG_CLICK_DELAY_MS);
    }
  }, []);

  // Selective dialog handlers
  const openSelectiveDialog = useCallback((tag) => {
    setSelectedTag(tag);
    setSelectedPlayersForTag([]);
    setOpenTagsDialog(true);
  }, []);

  const togglePlayerForTag = useCallback((playerId) => {
    setSelectedPlayersForTag((prev) =>
      prev.includes(playerId) ? prev.filter((id) => id !== playerId) : [...prev, playerId],
    );
  }, []);

  const selectAllPlayersForTag = useCallback((allPlayerIds) => {
    setSelectedPlayersForTag((prev) =>
      prev.length === allPlayerIds.length ? [] : allPlayerIds,
    );
  }, []);

  const closeDialog = useCallback(() => {
    setOpenTagsDialog(false);
    setSelectedTag(null);
    setSelectedPlayersForTag([]);
  }, []);

  const reset = useCallback(() => {
    setOpenTagsDialog(false);
    setSelectedTag(null);
    setSelectedPlayersForTag([]);
  }, []);

  return {
    // state
    availableTags,
    loadingTags,
    openTagsDialog,
    selectedTag,
    selectedPlayersForTag,

    // builders / operations
    buildTagAction,
    handleTagClick,
    openSelectiveDialog,
    togglePlayerForTag,
    selectAllPlayersForTag,
    closeDialog,
    reset,
  };
}
